import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { z } from 'zod';
import {
  ApiErrorEnvelope,
  AssignmentPageResponse,
  AssignmentRecord,
  AuditPageResponse,
  AuditRecord,
  CredentialGenerationResponse,
  CredentialGenerationWorkflow,
  DecommissionResponse,
  DeviceType,
  EquipmentPageResponse,
  EquipmentRecord,
  EquipmentType,
  HardwareRegistryPageResponse,
  HealthResponse,
  IssuanceMode,
  OperationalEnvironment,
  RuntimeDurationMinutes,
  SessionPageResponse,
  SessionRecord,
} from './types';

function queryString(input: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined && value !== '') query.set(key, value);
  }
  const serialized = query.toString();
  return serialized ? `?${serialized}` : '';
}

const deviceTypeSchema = z
  .object({
    id: z.string(),
    code: z.string(),
    displayName: z.string(),
    active: z.boolean(),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
  })
  .strict();

const hardwareRegistryPageSchema = z
  .object({
    records: z.array(
      z
        .object({
          id: z.string(),
          hardwareId: z.string(),
          deviceType: z
            .object({ code: z.string(), displayName: z.string() })
            .strict(),
          certificateEnvironment: z.enum(['GLOBAL_QA2', 'GLOBAL_DEV']),
          // Matches the backend contract: three operational environments, and
          // nullable for legacy hardware rows created before the column existed.
          operationalEnvironment: z.enum(['QA2', 'Dev', 'Test2']).nullable(),
          credentialStatus: z.string(),
          availability: z.enum(['Available', 'Assigned']),
          currentAssignment: z
            .object({
              assignmentId: z.string(),
              equipmentId: z.string(),
              equipmentNumber: z.string(),
              equipmentName: z.string(),
            })
            .strict()
            .nullable(),
          createdAt: z.string().datetime(),
        })
        .strict(),
    ),
    nextCursor: z.string().optional(),
    endOfResults: z.boolean(),
  })
  .strict();

export class ProtectedStateError extends Error {
  constructor(public readonly status: 401 | 403) {
    super('Your Test Equipments session is no longer authorized.');
    this.name = 'ProtectedStateError';
  }
}

export class TestEquipmentsClient {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async health(): Promise<HealthResponse> {
    return this.request<HealthResponse>('/health');
  }

  async listDeviceTypes(): Promise<DeviceType[]> {
    const value = await this.request<unknown>('/device-types');
    return z.array(deviceTypeSchema).parse(value);
  }

  async listHardware(
    input: {
      pageSize?: number;
      cursor?: string;
      search?: string;
      deviceType?: string;
      certificateEnvironment?: 'GLOBAL_QA2' | 'GLOBAL_DEV';
      operationalEnvironment?: OperationalEnvironment;
      credentialStatus?: string;
      availability?: 'Available' | 'Assigned';
    } = {},
  ): Promise<HardwareRegistryPageResponse> {
    const query = new URLSearchParams();
    for (const [key, value] of Object.entries(input)) {
      if (value !== undefined) query.set(key, String(value));
    }
    const value = await this.request<unknown>(
      `/hardware${query.size ? `?${query.toString()}` : ''}`,
    );
    return hardwareRegistryPageSchema.parse(value);
  }

  async startCredentialGeneration(input: {
    operationalEnvironment?: OperationalEnvironment;
    certificateEnvironment?: 'GLOBAL_QA2' | 'GLOBAL_DEV';
    deviceType: string;
    hardwareId: string;
    issuanceMode?: IssuanceMode;
    idempotencyKey?: string;
  }): Promise<CredentialGenerationResponse> {
    const { idempotencyKey, ...request } = input;
    return this.request<CredentialGenerationResponse>(
      '/credential-generations',
      {
        method: 'POST',
        headers: {
          'content-type': 'application/json',
          ...(idempotencyKey ? { 'idempotency-key': idempotencyKey } : {}),
        },
        body: JSON.stringify(request),
      },
    );
  }

  async getCredentialGeneration(
    workflowId: string,
  ): Promise<CredentialGenerationWorkflow> {
    return this.request<CredentialGenerationWorkflow>(
      `/credential-generations/${encodeURIComponent(workflowId)}`,
    );
  }

  async downloadManualCsr(workflowId: string): Promise<string> {
    const response = await this.response(
      `/credential-generations/${encodeURIComponent(workflowId)}/manual-csr`,
    );
    return response.text();
  }

  async uploadManualCertificate(
    workflowId: string,
    certificatePem: string,
  ): Promise<CredentialGenerationWorkflow> {
    return this.request<CredentialGenerationWorkflow>(
      `/credential-generations/${encodeURIComponent(
        workflowId,
      )}/manual-certificate`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/x-pem-file' },
        body: certificatePem,
      },
    );
  }

  async uploadCredentials(input: {
    operationalEnvironment: OperationalEnvironment;
    deviceType: string;
    hardwareId: string;
    privateKeyFile: File;
    certificateFile: File;
  }): Promise<CredentialGenerationWorkflow> {
    const [privateKey, certificate] = await Promise.all([
      fileToBase64(input.privateKeyFile),
      fileToBase64(input.certificateFile),
    ]);
    return this.request<CredentialGenerationWorkflow>('/credential-uploads', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        operationalEnvironment: input.operationalEnvironment,
        deviceType: input.deviceType,
        hardwareId: input.hardwareId,
        privateKey,
        certificate,
      }),
    });
  }

  async listEquipment(
    input: {
      search?: string;
      tribe?: string;
      productTeam?: string;
      equipmentType?: EquipmentType;
      operationalEnvironment?: OperationalEnvironment;
    } = {},
  ): Promise<EquipmentPageResponse> {
    return this.request<EquipmentPageResponse>(
      `/equipment${queryString(input)}`,
    );
  }

  async createEquipment(input: {
    equipmentIdentifier: string;
    name: string;
    operationalEnvironment: OperationalEnvironment;
    equipmentType: EquipmentType;
    controlSystem?: string | null;
    mbmTechnicalPlatform?: string | null;
    tribe?: string | null;
    productTeam?: string | null;
    manufacturer?: 'KONE' | 'OTIS' | 'TKE' | 'SCHINDLER' | null;
  }): Promise<EquipmentRecord> {
    return this.request<EquipmentRecord>('/equipment', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async updateEquipment(
    equipmentId: string,
    input: {
      name?: string;
      equipmentType?: EquipmentType;
      controlSystem?: string | null;
      mbmTechnicalPlatform?: string | null;
      tribe?: string | null;
      productTeam?: string | null;
      manufacturer?: 'KONE' | 'OTIS' | 'TKE' | 'SCHINDLER' | null;
    },
  ): Promise<EquipmentRecord> {
    return this.request<EquipmentRecord>(
      `/equipment/${encodeURIComponent(equipmentId)}`,
      {
        method: 'PATCH',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(input),
      },
    );
  }

  async deleteEquipment(equipmentId: string): Promise<void> {
    await this.response(`/equipment/${encodeURIComponent(equipmentId)}`, {
      method: 'DELETE',
    });
  }

  async deleteHardware(
    hardwareId: string,
    operationalEnvironment: OperationalEnvironment,
  ): Promise<{ hardwareId: string; secretsRemoved: 'deleted' | 'unconfirmed' | 'none' }> {
    return this.request<{
      hardwareId: string;
      secretsRemoved: 'deleted' | 'unconfirmed' | 'none';
    }>(
      `/hardware/${encodeURIComponent(hardwareId)}${queryString({
        operationalEnvironment,
      })}`,
      { method: 'DELETE' },
    );
  }

  async listAssignments(
    input: {
      search?: string;
      active?: 'true' | 'false';
      operationalEnvironment?: OperationalEnvironment;
    } = {},
  ): Promise<AssignmentPageResponse> {
    return this.request<AssignmentPageResponse>(
      `/assignments${queryString(input)}`,
    );
  }

  async createAssignment(input: {
    equipmentId: string;
    hardwareRecordId: string;
    operationalEnvironment: OperationalEnvironment;
  }): Promise<AssignmentRecord> {
    return this.request<AssignmentRecord>('/assignments', {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(input),
    });
  }

  async startRuntime(input: {
    assignmentId: string;
    runtimeDurationMinutes?: RuntimeDurationMinutes;
  }): Promise<SessionRecord> {
    return this.request<SessionRecord>(
      `/assignments/${encodeURIComponent(
        input.assignmentId,
      )}/capabilities/simulator-runtime/sessions`,
      {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(
          input.runtimeDurationMinutes
            ? { runtimeDurationMinutes: input.runtimeDurationMinutes }
            : {},
        ),
      },
    );
  }

  async stopRuntime(sessionId: string): Promise<SessionRecord> {
    return this.request<SessionRecord>(
      `/sessions/${encodeURIComponent(sessionId)}/actions/stop`,
      { method: 'POST' },
    );
  }

  async getSession(sessionId: string): Promise<SessionRecord> {
    return this.request<SessionRecord>(
      `/sessions/${encodeURIComponent(sessionId)}`,
    );
  }

  async listSessions(
    input: { assignmentId?: string; active?: 'true' | 'false' } = {},
  ): Promise<SessionPageResponse> {
    return this.request<SessionPageResponse>(`/sessions${queryString(input)}`);
  }

  /**
   * Issues the cookie the browser needs to reach the simulator UI in a new tab,
   * which carries no bearer token of its own.
   */
  async issueSimulatorUiCookie(sessionId: string): Promise<void> {
    await this.response(
      `/sessions/${encodeURIComponent(sessionId)}/ui-cookie`,
      { method: 'POST', credentials: 'include' },
    );
  }

  /**
   * Builds the simulator UI address. A fresh token keeps each opened tab unique in
   * the address bar; the backend ignores it, so a refresh resolves to the same
   * session.
   */
  async getSimulatorUiUrl(sessionId: string): Promise<string> {
    const baseUrl = await this.discoveryApi.getBaseUrl('test-equipments');
    return `${baseUrl}/sessions/${encodeURIComponent(
      sessionId,
    )}/ui/?session=${crypto.randomUUID()}`;
  }

  async setUsageNote(sessionId: string, note: string): Promise<SessionRecord> {
    return this.request<SessionRecord>(
      `/sessions/${encodeURIComponent(sessionId)}/usage-note`,
      {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ note }),
      },
    );
  }

  async clearUsageNote(sessionId: string): Promise<SessionRecord> {
    return this.request<SessionRecord>(
      `/sessions/${encodeURIComponent(sessionId)}/usage-note`,
      { method: 'DELETE' },
    );
  }

  async decommissionAssignment(
    assignmentId: string,
  ): Promise<DecommissionResponse> {
    return this.request<DecommissionResponse>(
      `/assignments/${encodeURIComponent(assignmentId)}/decommission`,
      { method: 'POST' },
    );
  }

  async listAudit(
    input: {
      search?: string;
      action?: string;
      actor?: string;
      cursor?: string;
    } = {},
  ): Promise<AuditPageResponse> {
    return this.request<AuditPageResponse>(`/audit${queryString(input)}`);
  }

  /**
   * The activity trail scoped to one equipment (create/update, assign, runtime
   * start/stop, decommission), newest first. Distinct from listAudit, which is the
   * plugin-wide log. The backend returns a bounded, non-paginated set.
   */
  async listEquipmentAudit(
    equipmentId: string,
  ): Promise<{ records: AuditRecord[] }> {
    return this.request<{ records: AuditRecord[] }>(
      `/equipment/${encodeURIComponent(equipmentId)}/audit`,
    );
  }

  private async request<T>(path: string, init?: RequestInit): Promise<T> {
    return (await this.response(path, init)).json() as Promise<T>;
  }

  private async response(path: string, init?: RequestInit): Promise<Response> {
    const baseUrl = await this.discoveryApi.getBaseUrl('test-equipments');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, init);
    if (response.status === 401 || response.status === 403) {
      throw new ProtectedStateError(response.status);
    }
    if (!response.ok) {
      const fallback: ApiErrorEnvelope = {
        error: {
          code: 'INTERNAL_ERROR',
          message: 'The request could not be completed.',
          requestId: 'unavailable',
        },
      };
      let envelope = fallback;
      try {
        envelope = (await response.json()) as ApiErrorEnvelope;
      } catch {
        // Never expose an untrusted upstream body.
      }
      throw new Error(envelope.error?.message ?? fallback.error.message);
    }
    return response;
  }
}

async function fileToBase64(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  let binary = '';
  bytes.forEach(byte => {
    binary += String.fromCharCode(byte);
  });
  return btoa(binary);
}
